import {
  createAction,
  createSlice,
  ThunkAction,
  unwrapResult,
} from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, Store } from "../store/store";

export interface Feedback {
  error: boolean;
  message: string;
}

export interface FeedbackState {
  items: Feedback[];
}

const FeedbackStateI: FeedbackState = {
  items: [],
};

export const showFeedback = createAction<Feedback>("/feedback/show");
export const hideFeedback = createAction("/feedback/hide");

const feedback = createSlice({
  name: "/feedback",
  initialState: FeedbackStateI,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(showFeedback, (state, action) => {
      state.items.unshift(action.payload);
    });
    builder.addCase(hideFeedback, (state) => {
      state.items.pop();
    });
  },
});

export const useFeedback = (): Feedback[] => {
  const dispatch = useDispatch();
  const items = useSelector((state: Store) => state.feedback.items);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (items.length) {
      timer = setTimeout(() => {
        dispatch(hideFeedback());
      }, 6000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [items]);

  return items;
};

type DispatchWithFeedback = {
  error?: string;
  success?: string;
};

type DispatchWithFeedbackFn = () => (
  action: ThunkAction<any, any, any, any>,
  messages?: DispatchWithFeedback
) => Promise<any>;

export const useDispatchWithFeedback: DispatchWithFeedbackFn = () => {
  const dispatch = useDispatch<AppDispatch>();

  return async (action, messages): Promise<any> => {
    try {
      await unwrapResult(await dispatch(action));
      if (messages?.success) {
        await dispatch(
          showFeedback({
            message: messages.success,
            error: false,
          })
        );
      }
    } catch (err) {
      const message = messages?.error ?? (err as Error)?.message;
      if (message) {
        await dispatch(
          showFeedback({
            message,
            error: true,
          })
        );
      }
      throw err;
    }
  };
};

export default feedback;
